import type { PlayerStageVisibility, StandingsGroup, StandingsRow } from "./api";

/** Eduardo 2026-09-24: mientras el admin no termina el wizard, el jugador
 * solo ve la lista de inscritos por categoría. */
export function playerStageLocked(visibility: PlayerStageVisibility | null | undefined): boolean {
  return Boolean(visibility) && visibility!.setup_complete === false;
}

export const PLAYER_STAGE_LOCKED_MESSAGE =
  "El organizador todavía está armando el torneo. Mientras tanto puedes ver a los jugadores inscritos.";

/** Home y tarjeta del torneo mientras el admin no termina el wizard: en vez
 * de «sin partidos», se explica por qué todavía no hay próximo partido. */
export const PLAYER_STAGE_NEXT_MATCH_MESSAGE =
  "El organizador todavía está armando el torneo. Cuando publique el Calendario, aquí verás tu próximo partido.";

/** Home: resultados de un torneo que el organizador sigue armando. */
export const PLAYER_STAGE_RESULTS_MESSAGE =
  "Los resultados aparecerán cuando el organizador termine de armar el torneo.";

export type PlayerStageSection = "schedule" | "draw" | "ranking";

/** Eduardo 2026-09-24: la sección se ve, con su aviso, mientras el admin
 * no termina el wizard. */
export const PLAYER_STAGE_SECTION_MESSAGES: Record<PlayerStageSection, string> = {
  schedule: "Cuando el organizador publique el Calendario, aparecerá en esta sección.",
  draw: "Cuando el organizador publique el Draw, aparecerá en esta sección.",
  ranking: "Cuando el organizador publique los grupos y el Draw, el Ranking aparecerá en esta sección.",
};

export interface PlayerCategorySection {
  key: string;
  name: string;
  /** Subsecciones por grupo (solo cuando la categoría ya tiene grupos activos). */
  groups: PlayerGroupSection[];
  /** Jugadores de la categoría sin grupo (o todos, si la categoría no tiene grupos). */
  rows: StandingsRow[];
  total: number;
  isMine: boolean;
}

export interface PlayerGroupSection {
  key: string;
  name: string;
  rows: StandingsRow[];
  isMine: boolean;
}

const NO_DIVISION = "__none__";

function byName(a: StandingsRow, b: StandingsRow): number {
  return a.display_name.localeCompare(b.display_name, "es", { numeric: true });
}

/** Jugadores del torneo por CATEGORÍA y, dentro de cada categoría, por grupo
 * activo (Eduardo 2026-09-24). Los grupos inactivos no aparecen; los
 * jugadores que quedan fuera de todo grupo van en "Sin grupo" de su
 * categoría; sin grupos, la categoría lista a todos sus inscritos. */
export function buildPlayerCategorySections(
  ranking: StandingsRow[],
  groups: StandingsGroup[],
  myParticipantId: number | null,
): PlayerCategorySection[] {
  const divisionOf = new Map<number, { key: string; name: string }>();
  const order: string[] = [];
  const rowsByDivision = new Map<string, StandingsRow[]>();
  for (const row of ranking) {
    const key = row.division_id == null ? NO_DIVISION : String(row.division_id);
    const name = row.division_name?.trim() || (row.division_id == null ? "Jugadores" : `Categoría ${row.division_id}`);
    if (!rowsByDivision.has(key)) {
      rowsByDivision.set(key, []);
      order.push(key);
    }
    rowsByDivision.get(key)!.push(row);
    divisionOf.set(row.participant_id, { key, name });
  }

  // Grupos activos con jugadores → a la categoría de sus filas.
  const groupsByDivision = new Map<string, PlayerGroupSection[]>();
  const grouped = new Set<number>();
  for (const group of groups) {
    if (group.group_id == null || group.is_active === false) continue;
    const rows = [...group.standings].sort((a, b) => a.position - b.position);
    if (rows.length === 0) continue;
    const first = rows.find((row) => divisionOf.has(row.participant_id));
    const key = first ? divisionOf.get(first.participant_id)!.key : NO_DIVISION;
    if (!rowsByDivision.has(key)) {
      rowsByDivision.set(key, []);
      order.push(key);
    }
    rows.forEach((row) => grouped.add(row.participant_id));
    const list = groupsByDivision.get(key) ?? [];
    list.push({
      key: String(group.group_id),
      name: group.group_name,
      rows,
      isMine: myParticipantId != null && rows.some((row) => row.participant_id === myParticipantId),
    });
    groupsByDivision.set(key, list);
  }

  return order.map((key) => {
    const all = rowsByDivision.get(key) ?? [];
    const groupSections = groupsByDivision.get(key) ?? [];
    const leftovers = all.filter((row) => !grouped.has(row.participant_id)).sort(byName);
    const rowsOut = groupSections.length > 0 ? leftovers : [...all].sort(byName);
    const first = all[0] ?? groupSections[0]?.rows[0];
    const name = first ? divisionOf.get(first.participant_id)?.name ?? "Jugadores" : "Jugadores";
    const total = groupSections.reduce((sum, g) => sum + g.rows.length, 0) + rowsOut.length;
    const isMine =
      myParticipantId != null &&
      (groupSections.some((g) => g.isMine) || rowsOut.some((row) => row.participant_id === myParticipantId));
    return { key, name, groups: groupSections, rows: rowsOut, total, isMine };
  });
}
